import { v4 as uuidv4 } from 'uuid';

import SineBrowserStorage from '@sine/sine-browser-storage';

import { OAUTH_RECTIFIER_ROUTE } from '../constants/routes';
import type { Location, OAuthResult } from './auth.types';

const storageKey = 'plugins-oauth-state';

export class OAuthRectifyError extends Error {}

export interface AuthUrlData {
  authorization_url: string;
  client_id: string;
  scope: string;
}

export interface StoredAuthState {
  rectifyLocation: Location;
  state: string;
  responseArgNames: {
    codeVar: string;
    stateVar: string;
    errorVar: string;
  };
  redirect_uri: string;
}

function storeState(state: StoredAuthState) {
  const { sessionStorage } = SineBrowserStorage.getInstance();
  sessionStorage.setItem(storageKey, JSON.stringify(state));
}

function getStoredState(): StoredAuthState | null {
  const { sessionStorage } = SineBrowserStorage.getInstance();
  const json = sessionStorage.getItem(storageKey);
  if (json) {
    return JSON.parse(json);
  } else {
    return null;
  }
}

interface OAuthRectifier {
  retrieveOAuthResult: () => OAuthResult;
  authCodeGrant: (
    rectifyLocation: Location,
    authUriData: AuthUrlData,
    responseArgNames?: { codeVar?: string; stateVar?: string; errorVar?: string },
    excludeTrailingSlash?: boolean,
  ) => void;
}

const oAuthRectifier: OAuthRectifier = {
  /**
   * Retrieve the result of the most-recently-completed OAuth flow.
   * This includes the location to redirect to and either an authorization code or an error string.
   */
  retrieveOAuthResult: () => {
    const storedState = getStoredState();
    if (!storedState) {
      throw new OAuthRectifyError('Stored OAuth rectifier state not found');
    }

    const params = new URLSearchParams(window.location.search);
    const uriCode = params.get(storedState.responseArgNames.codeVar);
    const uriState = params.get(storedState.responseArgNames.stateVar);
    const uriError = params.get(storedState.responseArgNames.errorVar);

    // Get and check the state
    if (!uriState) {
      throw new OAuthRectifyError('URI state not found');
    }

    // Check for a code or error
    if (!uriCode && !uriError) {
      throw new OAuthRectifyError('URI code or error not found');
    }

    const { rectifyLocation, state } = storedState;
    if (state !== uriState) {
      throw new OAuthRectifyError('URI state did not match session storage state');
    }

    return {
      location: rectifyLocation,
      code: uriCode,
      error: uriError,
      redirect_uri: storedState.redirect_uri,
    };
  },

  /**
   * Initiate auth code grant request flow
   * Specified here https://tools.ietf.org/html/rfc6749#section-1.3.1
   * OAuth 2.0 flows should use this pattern in getting a code from
   * the user
   *
   * Will store the provided `rectifyLocation` to be redirected to after the OAuth flow is completed.
   * This location can be accessed after the user is redirected back from the OAuth flow using {@link retrieveOAuthResult }.
   */
  authCodeGrant: function (
    rectifyLocation,
    authUriData,
    { codeVar = 'code', stateVar = 'state', errorVar = 'error' } = {},
    excludeTrailingSlash = false,
  ) {
    const state = uuidv4();

    let redirectUri = `${window.location.origin}${window.env.PUBLIC_URL}${OAUTH_RECTIFIER_ROUTE}`;
    if (excludeTrailingSlash && redirectUri.endsWith('/')) {
      redirectUri = redirectUri.slice(0, redirectUri.length - 1);
    }

    storeState({
      rectifyLocation,
      state: state,
      responseArgNames: {
        codeVar,
        stateVar,
        errorVar,
      },
      redirect_uri: redirectUri,
    });

    const oAuthUrl = new URL(authUriData.authorization_url);
    const oAuthParams = {
      client_id: authUriData.client_id,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: authUriData.scope,
      state,
    };
    Object.entries(oAuthParams).forEach(([key, value]) => oAuthUrl.searchParams.set(key, value));

    window.location.href = oAuthUrl.toString();
  },
};

export default oAuthRectifier;
